import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { serviceImage } from "../../lib/api";
import { useContent, pick } from "../../lib/content";
import { useSite } from "../../lib/site";
import { useServices } from "../../lib/services";
import SEO from "../../components/SEO";
import CTABand from "../../components/public/CTABand";
import FAQSection from "../../components/public/FAQSection";

export default function Services() {
  const c = useContent();
  const site = useSite();
  const services = useServices();

  return (
    <div data-testid="services-page">
      <SEO
        path="/services"
        title="Services — Decks, Kitchens, Baths, Roofing & More"
        description={`Full-service home improvement in Loganville, PA. ${services.map((s) => s.title).slice(0, 6).join(" · ")}. Lifetime labor warranty.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": services.map((s, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": s.title,
            "url": `${(site?.site_url || "").replace(/\/$/, "")}/services/${s.slug}`,
          })),
        }}
      />
      <section className="bg-atc-black text-white pt-20 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-grid-dark bg-[size:4rem_4rem] opacity-30" />
        <div className="container-max relative">
          <div className="overline mb-4 text-atc-red">{pick(c, "services_page.hero_overline", "Our services")}</div>
          <h1 className="h-display">{pick(c, "services_page.hero_title_line_1", "Inside. Outside.")}<br/>{pick(c, "services_page.hero_title_line_2", "Everywhere.")}</h1>
          <p className="mt-6 text-white/80 text-lg max-w-2xl">{pick(c, "services_page.hero_body", "")}</p>
        </div>
      </section>

      <section className="section-pad bg-atc-paper" data-testid="services-grid">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc) => (
              <Link
                to={`/services/${svc.slug}`}
                key={svc.id}
                className="group block bg-white border-2 border-atc-black relative overflow-hidden transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#1A1A1A]"
                data-testid={`service-card-${svc.slug}`}
              >
                <div className="aspect-[4/3] overflow-hidden bg-atc-black">
                  <img src={serviceImage(svc)} alt={svc.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="overline mb-2">ATC · Service</div>
                  <h3 className="h-card">{svc.title}</h3>
                  <p className="mt-3 text-sm text-atc-muted">{svc.short_description}</p>
                  <div className="mt-5 inline-flex items-center gap-2 font-heading uppercase tracking-widest text-xs group-hover:text-atc-red">
                    Learn more <ArrowUpRight size={14} weight="bold" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQSection limit={8} title="Frequently asked" subtitle="Common questions homeowners across York County ask before signing on with a contractor." />

      <CTABand phone={site?.phone} />
    </div>
  );
}
